import { z } from "zod/v4";
import {
	closureIdSchema,
	courtIdSchema,
	localDateSchema,
	priceRuleIdSchema,
	wallTimeSchema,
} from "./ids";
import { currencySchema } from "./venue";

export const sportSchema = z.enum(["beach", "padel", "tennis"]);
export const surfaceSchema = z.enum(["sand", "hard", "grass", "artificial"]);
export const courtStatusSchema = z.enum(["active", "maintenance", "closed"]);

export const courtSchema = z.object({
	bookable: z.boolean(),
	id: courtIdSchema,
	name: z.string(),
	sortOrder: z.number().int(),
	sport: sportSchema,
	status: courtStatusSchema,
	surface: surfaceSchema,
});

export const createCourtSchema = z.object({
	bookable: z.boolean(),
	name: z.string().trim().min(1).max(120),
	sortOrder: z.number().int(),
	sport: sportSchema,
	surface: surfaceSchema,
});
export const updateCourtSchema = createCourtSchema.extend({ status: courtStatusSchema });

/** 0 = Monday … 6 = Sunday (the API's and the database's convention). */
export const weekdaySchema = z.number().int().min(0).max(6);

export const hourEntrySchema = z.object({
	closes: wallTimeSchema,
	opens: wallTimeSchema,
	weekday: weekdaySchema,
});

export const closureWindowSchema = z.object({ closes: wallTimeSchema, opens: wallTimeSchema });
export const closureSchema = z.object({
	date: localDateSchema,
	id: closureIdSchema,
	/** `null` = closed all day. */
	window: closureWindowSchema.nullable(),
});
export const createClosureSchema = z.object({
	date: localDateSchema,
	window: closureWindowSchema.nullable(),
});

export const priceRuleBodySchema = z.object({
	courtId: courtIdSchema.nullable(),
	currency: currencySchema,
	ends: wallTimeSchema,
	priority: z.number().int(),
	ratePerHourCents: z.number().int().positive(),
	starts: wallTimeSchema,
	validFrom: localDateSchema.nullable(),
	validUntil: localDateSchema.nullable(),
	weekdays: z.array(weekdaySchema).min(1),
});
export const priceRuleSchema = priceRuleBodySchema.extend({ id: priceRuleIdSchema });

export type Sport = z.infer<typeof sportSchema>;
export type Surface = z.infer<typeof surfaceSchema>;
export type CourtStatus = z.infer<typeof courtStatusSchema>;
export type Court = z.infer<typeof courtSchema>;
export type CreateCourt = z.infer<typeof createCourtSchema>;
export type UpdateCourt = z.infer<typeof updateCourtSchema>;
export type Weekday = z.infer<typeof weekdaySchema>;
export type HourEntry = z.infer<typeof hourEntrySchema>;
export type Closure = z.infer<typeof closureSchema>;
export type CreateClosure = z.infer<typeof createClosureSchema>;
export type PriceRuleBody = z.infer<typeof priceRuleBodySchema>;
export type PriceRule = z.infer<typeof priceRuleSchema>;
